import React, { useState, useEffect } from 'react';
import { skillService } from '../../services/skillService';
import { useAuth } from '../../context/AuthContext';
import './SkillList.css';

const SkillList = () => {
  const { user } = useAuth();
  const [skills, setSkills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingSkill, setEditingSkill] = useState(null);
  const [formData, setFormData] = useState({
    skill_name: '',
    category: ''
  });

  const categories = [
    'Programming',
    'Frontend',
    'Backend',
    'Database',
    'Cloud',
    'DevOps',
    'Mobile',
    'Testing',
    'Version Control',
    'API',
    'Architecture',
    'Methodology',
    'Other'
  ];

  useEffect(() => {
    loadSkills();
  }, []);

  const loadSkills = async () => {
    try {
      setLoading(true);
      const response = await skillService.getAllSkills();
      const data = Array.isArray(response) ? response : (response.data || []);
      setSkills(data);
    } catch (err) {
      setError(err.message || 'Failed to load skills');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingSkill(null);
    setFormData({ skill_name: '', category: '' });
    setShowModal(true);
  };

  const handleEdit = (skill) => {
    setEditingSkill(skill);
    setFormData({
      skill_name: skill.skill_name,
      category: skill.category || ''
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this skill? This will remove it from all profiles.')) return;

    try {
      await skillService.deleteSkill(id);
      await loadSkills();
    } catch (err) {
      setError(err.message || 'Failed to delete skill');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      if (editingSkill) {
        await skillService.updateSkill(editingSkill.id, formData);
      } else {
        await skillService.createSkill(formData);
      }
      setShowModal(false);
      await loadSkills();
    } catch (err) {
      setError(err.message || 'Failed to save skill');
    }
  };

  const skillsByCategory = skills.reduce((acc, skill) => {
    const cat = skill.category || 'Uncategorized';
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(skill);
    return acc;
  }, {});

  const canManage = user?.role === 'hr_admin' || user?.role === 'admin' || user?.role === 'manager';

  if (!canManage) {
    return (
      <div className="skills-container">
        <div className="error-message">You don't have permission to manage skills.</div>
      </div>
    );
  }

  return (
    <div className="skills-container">
      <div className="skills-header">
        <div>
          <h1>Master Skills</h1>
          <p>Manage the master list of skills</p>
        </div>
        <button onClick={handleAdd} className="btn-primary">
          + Add Skill
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {loading ? (
        <div className="loading">Loading skills...</div>
      ) : (
        <div className="skills-categories">
          {Object.entries(skillsByCategory).sort().map(([category, categorySkills]) => (
            <div key={category} className="category-section">
              <h2>{category} ({categorySkills.length})</h2>
              <div className="skills-grid">
                {categorySkills.map(skill => (
                  <div key={skill.id} className="skill-item">
                    <span className="skill-name">{skill.skill_name}</span>
                    <div className="skill-actions">
                      <button onClick={() => handleEdit(skill)} className="btn-icon" title="Edit">
                        ✏️
                      </button>
                      <button onClick={() => handleDelete(skill.id)} className="btn-icon" title="Delete">
                        🗑️
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h2>{editingSkill ? 'Edit Skill' : 'Add New Skill'}</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label>Skill Name *</label>
                <input
                  type="text"
                  value={formData.skill_name}
                  onChange={e => setFormData({...formData, skill_name: e.target.value})}
                  required
                  placeholder="e.g., React, Python, AWS"
                />
              </div>

              <div className="form-group">
                <label>Category *</label>
                <select
                  value={formData.category}
                  onChange={e => setFormData({...formData, category: e.target.value})}
                  required
                >
                  <option value="">Select category...</option>
                  {categories.map(cat => (
                    <option key={cat} value={cat}>{cat}</option>
                  ))}
                </select>
              </div>

              <div className="modal-actions">
                <button type="submit" className="btn-primary">
                  {editingSkill ? 'Update' : 'Add'} Skill
                </button>
                <button type="button" onClick={() => setShowModal(false)} className="btn-secondary">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SkillList;
